'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const classnames = require('classnames');

const { noop } = require('../../../mixins/commonjs/basicUtils');
const PageModel = require('../models/PageModel.es');
const BaseCarousel = require('../base/BaseCarousel');
const ArrowCarouselComponent = require('./ArrowCarouselComponent');
const DotCarousel = require('./DotCarousel');

/**
 * Represents a carousel with arrows for paging through the list.
 * @class
 */
class ArrowCarousel extends React.Component {
    constructor(props) {
        super(props);

        // Todo refactor model logic into a store
        this._model = new PageModel({
            itemsPerPage: props.itemsPerPage,
            itemCount: React.Children.count(props.children),
            onPageChange: props.onPageChange,
            startingPage: props.startingPage,
            transitionEffect: props.transitionEffect,
            // Force a re-render if the model changes.
            onStateChange: modelState => this.setState({ modelState }),
            customTransformItemStyle: props.customTransformItemStyle,
        });

        //// Set the initial model state. This is probably unnecessary but put here to avoid issues if state gets used
        //// in `render` in the future.
        this.state = {
            modelState: this._model.getState(),
        };

        this._onLeftArrowClick = this._onLeftArrowClick.bind(this);
        this._onRightArrowClick = this._onRightArrowClick.bind(this);
    }

    /**
     * Imperatively set the page from outside the component, e.g. if the state of this carousel needs to be kept
     * constistent with another carousel.
     *
     * @param {number} page - The page to set.
     * @param {object} model - Model to update, needed for components where this method gets passed.
     * @param {function} callback
     */
    setPage(page, model = this._model, callback) {
        // Update the model which will in turn trigger an update to the view state and force a re-render.
        if (callback) {
            callback();
        }

        model.jumpToPage(page);
    }

    /**
     * Method to handle the left carousel arrow click.
     */
    _onLeftArrowClick() {
        const { onLeftArrowClick } = this.props;

        this._model.previousPage();

        if (onLeftArrowClick) {
            onLeftArrowClick(this._model.getState().page);
        }
    }

    /**
     * Method to handle the right carousel arrow click.
     */
    _onRightArrowClick() {
        const { onRightArrowClick } = this.props;

        this._model.nextPage();

        if (onRightArrowClick) {
            onRightArrowClick(this._model.getState().page);
        }
    }

    /**
     * Method to create a basic carousel which then can be shown or wrapped by other components.
     *
     * @returns {object} - JSX of a carousel.
     */
    _createCarousel() {
        return (
            <BaseCarousel
                model={this._model}
                classNames={{ list: this._listClass, item: this._itemClass }}
            >
                {this.props.children}
            </BaseCarousel>
        );
    }

    render() {
        const { classNames: { list, item }, auto, useDotsNav } = this.props;
        const canPaginate = this._model.canPaginate();

        this._listClass = classnames({
            ['arrow-carousel-list']: true,
            [list]: list,
        });
        this._itemClass = classnames({
            ['arrow-carousel-item']: true,
            [item]: item,
        });

        if (auto || useDotsNav) {
            return (
                <DotCarousel
                    model={this._model}
                    classNames={this.props.classNames}
                    createCarousel={() => this._createCarousel()}
                    setPage={this.setPage}
                    autoDuration={this.props.autoDuration}
                    dotsPosition={this.props.dotsPosition}
                    dotsColorType={this.props.dotsColorType}
                    onDotClick={this.props.onDotClick}
                    auto={auto}
                />
            );
        }

        return (
            <ArrowCarouselComponent
                showArrows={canPaginate}
                createCarousel={() => this._createCarousel()}
                onLeftArrowClick={() => this._onLeftArrowClick()}
                onRightArrowClick={() => this._onRightArrowClick()}
                classNames={this.props.classNames}
            />
        );
    }
}

ArrowCarousel.propTypes = {
    onPageChange: PropTypes.func,
    // Change the number of items to display per carousel page.
    itemsPerPage: PropTypes.number,
    startingPage: PropTypes.number,
    transitionEffect: PropTypes.string,
    classNames: PropTypes.object,
    children: PropTypes.node,
    useDotsNav: PropTypes.bool,
    auto: PropTypes.bool,
    autoDuration: PropTypes.number,
    dotsColorType: PropTypes.oneOf(['buyer', 'trade']),
    dotsPosition: PropTypes.oneOf(['left', 'right']),
    onDotClick: PropTypes.func.isRequired,
    onLeftArrowClick: PropTypes.func,
    onRightArrowClick: PropTypes.func,
    customTransformItemStyle: PropTypes.func,
};

ArrowCarousel.defaultProps = {
    autoDuration: 5000,
    itemsPerPage: 1,
    onPageChange: noop,
    onDotClick: noop,
    startingPage: 0,
    classNames: {},
    auto: false,
};

module.exports = ArrowCarousel;



// WEBPACK FOOTER //
// ./assets/js/global/components/carousel/views/ArrowCarousel.jsx